import {
  api_add_payment_method_path,
  api_create_free_subscription_path,
  api_create_payment_intent_path,
  api_create_setup_intent_path,
  api_create_subscription_path,
  api_customer_info_path,
  api_estimate_tax_calculation_path,
  api_payment_methods_list_path,
  api_remove_payment_method_path,
  api_update_payment_method_path,
  api_update_payment_method_to_default_path,
  api_update_stripe_customer_path,
} from '@routes';
import { Address } from '@stripe/stripe-js';

import request from './request';
import { SubscriptionResponse } from './subscribe';

export interface SubscriptionParams {
  code: string;
  modal_guid?: string;
  page: string;
  presented_offer_codes: string[];
  referer: string;
  referring_article_id?: string;
  referrer_guid?: string;
  stripe_customer_id: string;
}

export interface PaymentMethod {
  id: string;
  billingAddress?: Address;
  card?: {
    brand?: string;
    last4?: string;
    expMonth?: number;
    expYear?: number;
  };
  paymentType: string;
  address?: Address;
  name?: string;
  isPrimary: boolean;
}

export const FETCH_STRIPE_CUSTOMER_KEY = 'fetchStripeCustomer';
export const PAYMENT_METHOD_LIST_KEY = 'PAYMENT_METHOD_LIST';

type SetupIntentParams = Omit<
  SubscriptionParams,
  'code' | 'page' | 'presented_offer_codes' | 'referer'
>;

interface StripeSetupIntentResponse {
  clientSecret: string;
}

interface StripeSubscriptionResponse extends StripeSetupIntentResponse {
  subscriptionId: string;
}

export interface EstimateTaxCalculationParams {
  code: string;
  stripe_customer_id: string;
  address?: Address;
}

type PaymentIntentParams = Omit<
  SubscriptionParams,
  'code' | 'page' | 'presented_offer_codes' | 'referer'
> & {
  amount: number;
  gift_recipient_stripe_customer_id: string;
  stripe_customer_id: string;
  subscription_start_date: string;
};

interface EstimateTaxCalculationResponse {
  taxAmount: number;
  totalWithTax: number;
}

export interface FetchStripeCustomerResponse {
  address: Address;
  firstName: string;
  lastName: string;
}

export interface FetchStripeCustomerRequest {
  stripe_customer_id: string;
}

export interface UpdateStripeCustomerLocationParams {
  address: Address;
  name: string;
  stripe_customer_id: string;
}

export interface UpdateStripeCustomerLocationResponse {
  success: boolean;
}

export interface FetchCustomerPaymentMethodListResponse {
  paymentMethodList: PaymentMethod[];
}

export interface RemovePaymentMethodParams {
  payment_method_id: string;
}

export interface RemovePaymentMethodResponse {
  success: boolean;
  message?: string;
}

export interface UpdatePaymentMethodParams {
  payment_method_id: string;
  exp_month: number;
  exp_year: number;
  address: Address;
  name: string;
}

export interface UpdatePaymentMethodResponse {
  success: boolean;
  message?: string;
}

export interface UpdatePaymentMethodToDefaultParams {
  payment_method_id: string;
  is_new_payment_method?: boolean;
  is_primary: boolean;
}

export interface UpdatePaymentResponse {
  redirect_to: string;
}

export const createSubscription = async (
  params: SubscriptionParams
): Promise<StripeSubscriptionResponse> => {
  const response = await request('post', api_create_subscription_path(params));
  return response;
};

export const createFreeSubscription = async (
  params: SubscriptionParams
): Promise<SubscriptionResponse> => {
  const response = await request('post', api_create_free_subscription_path(params));
  return response;
};

export const createSetupIntent = async (
  params: SetupIntentParams
): Promise<StripeSetupIntentResponse> => {
  const response = await request('post', api_create_setup_intent_path(params));
  return response;
};

export const createPaymentIntent = async (
  params: PaymentIntentParams
): Promise<StripeSetupIntentResponse> => {
  const response = await request('post', api_create_payment_intent_path(params));
  return response;
};

export const estimateTaxCalculation = async (
  params: EstimateTaxCalculationParams
): Promise<EstimateTaxCalculationResponse> => {
  const response = await request('post', api_estimate_tax_calculation_path(params));
  return response;
};

export const updateStripeCustomerLocation = async (
  params: UpdateStripeCustomerLocationParams
): Promise<UpdateStripeCustomerLocationResponse> => {
  const response = await request('put', api_update_stripe_customer_path(params));
  return response;
};

export const fetchStripeCustomer = async (
  params: FetchStripeCustomerRequest
): Promise<FetchStripeCustomerResponse> => {
  const response = await request('get', api_customer_info_path(params));
  return response;
};

export const fetchCustomerPaymentMethodList =
  async (): Promise<FetchCustomerPaymentMethodListResponse> => {
    const response = await request('get', api_payment_methods_list_path());
    return response;
  };

export const removePaymentMethod = async (
  params: RemovePaymentMethodParams
): Promise<RemovePaymentMethodResponse> => {
  const response = await request('delete', api_remove_payment_method_path(params));
  return response;
};

export const updatePaymentMethod = async (
  params: UpdatePaymentMethodParams
): Promise<UpdateStripeCustomerLocationResponse> => {
  const response = await request('put', api_update_payment_method_path(params));
  return response;
};

export const updatePaymentMethodToDefault = async (
  params: UpdatePaymentMethodToDefaultParams
): Promise<UpdatePaymentMethodResponse> => {
  const response = await request('put', api_update_payment_method_to_default_path(params));
  return response;
};

export interface AddPaymentMethodParams {
  payment_method_id?: string;
}

export async function addPaymentMethod(
  params: AddPaymentMethodParams = {}
): Promise<UpdatePaymentResponse> {
  const response = await request('post', api_add_payment_method_path(), params);
  return response;
}
